import { z } from 'zod'
import {
  DIFICULTADES,
  INFORMANTES,
  LUGARES_INICIO,
  MODOS,
  MODOS_CAPTURA,
  MONEDAS,
  NIVELES,
  PARENTESCOS,
  RANGOS_DURACION,
  TARIFA_ESTUDIANTIL,
  valores,
} from './catalogos.js'

/**
 * Contrato de una respuesta tal como la envían la PWA y la app. La API la
 * valida con este mismo esquema; las reglas de negocio (coherencias) viven en
 * `validaciones.ts` y se aplican encima.
 */

const enumDe = <V extends string>(lista: readonly { valor: V }[]) =>
  z.enum(valores(lista) as [V, ...V[]])

export const HORA = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Hora inválida (HH:MM)')
/** Montos como texto decimal para no perder precisión (nunca float). */
export const MONTO = z
  .string()
  .regex(/^\d{1,12}(\.\d{1,4})?$/, 'Monto inválido')
  .or(z.number().nonnegative().transform((n) => String(n)))

export const zMonto = z.object({ monto: MONTO, moneda: enumDe(MONEDAS) })
export type MontoMoneda = z.infer<typeof zMonto>

export const zCoordenada = z.object({
  lat: z.number().min(-90).max(90),
  lng: z.number().min(-180).max(180),
})

export const zTramo = z.object({
  sentido: z.enum(['ida', 'regreso']),
  orden: z.number().int().min(1).max(5),
  modo: enumDe(MODOS),
  minutos: z.number().int().min(1).max(240),
  gasto: zMonto.optional(),
})
export type Tramo = z.infer<typeof zTramo>

export const zOrigen = z.object({
  codParroquia: z.string().regex(/^\d{6}$/),
  localidad: z.string().trim().min(2).max(80),
  sectorId: z.string().uuid().optional(),
  sectorTexto: z.string().trim().max(80).optional(),
  /** Celda de privacidad (cuadrícula ~500 m). Nunca la coordenada cruda. */
  celda: z.string().regex(/^C-?\d+_-?\d+$/).optional(),
  /** Punto YA AJUSTADO (centroide de sector o de celda). */
  punto: zCoordenada.optional(),
  metodo: z.enum(['mapa', 'lista', 'texto']),
})
export type Origen = z.infer<typeof zOrigen>

export const zInstitucionPropuesta = z.object({
  nombre: z.string().trim().min(4).max(160),
  direccion: z.string().trim().min(4).max(240),
  codParroquia: z.string().regex(/^\d{6}$/),
  niveles: z.array(enumDe(NIVELES)).min(1),
  punto: zCoordenada.optional(),
})

export const zRespuesta = z
  .object({
    id: z.string().uuid(),
    campanaId: z.string().uuid(),
    formularioVersion: z.string().min(1).max(20),
    modoCaptura: z.enum(MODOS_CAPTURA),
    idOrganismo: z.string().trim().max(30).optional(),

    informante: enumDe(INFORMANTES),
    parentesco: enumDe(PARENTESCOS).optional(),
    consentimiento: z.object({
      aceptado: z.literal(true),
      version: z.string().min(1).max(20),
      aceptadoEn: z.string().datetime({ offset: true }),
    }),
    asentimiento: z.boolean().optional(),

    nombre: z.string().trim().max(80).optional(),
    edad: z.number().int().min(2).max(99),
    nivel: enumDe(NIVELES),
    nivelOtro: z.string().trim().max(60).optional(),
    grado: z.string().trim().max(20).optional(),

    institucionId: z.string().uuid().optional(),
    institucionPropuesta: zInstitucionPropuesta.optional(),

    lugarInicio: enumDe(LUGARES_INICIO),
    lugarInicioOtro: z.string().trim().max(60).optional(),
    origen: zOrigen,

    diasSemana: z.number().int().min(1).max(7),
    horaSalidaIda: HORA,
    horaLlegadaIda: HORA,
    modoIda: enumDe(MODOS),
    modoIdaOtro: z.string().trim().max(60).optional(),

    regresoIgualIda: z.boolean().optional(),
    horaSalidaRegreso: HORA,
    horaLlegadaRegreso: HORA,
    modoRegreso: enumDe(MODOS),
    modoRegresoOtro: z.string().trim().max(60).optional(),

    tramos: z.array(zTramo).max(10).default([]),

    gastoIda: zMonto,
    gastoRegreso: zMonto,
    tarifaEstudiantil: enumDe(TARIFA_ESTUDIANTIL).optional(),

    declarados: z
      .object({
        rangoIda: enumDe(RANGOS_DURACION).optional(),
        rangoRegreso: enumDe(RANGOS_DURACION).optional(),
        diasMes: z.number().int().min(1).max(31).optional(),
        gastoMensual: zMonto.optional(),
      })
      .default({}),

    dificultadPrincipal: enumDe(DIFICULTADES),
    dificultadOtro: z.string().trim().max(80).optional(),
    dificultadesOtras: z.array(enumDe(DIFICULTADES)).max(3).default([]),

    observaciones: z.string().trim().max(300).optional(),

    captura: z
      .object({
        inicio: z.string().datetime({ offset: true }),
        fin: z.string().datetime({ offset: true }),
        gps: zCoordenada.extend({ precision: z.number().nonnegative().max(10000) }).optional(),
        motivoDuplicado: z.string().trim().max(200).optional(),
      })
      .optional(),

    /** Tasa que usó el dispositivo (offline); el servidor recalcula con la oficial. */
    tasaDispositivo: z
      .object({ fecha: z.string().regex(/^\d{4}-\d{2}-\d{2}$/), valor: MONTO })
      .optional(),

    capturadoEn: z.string().datetime({ offset: true }),
  })
  .strict()
  .refine((r) => r.institucionId || r.institucionPropuesta, {
    message: 'Debe indicar la institución o proponer una nueva',
    path: ['institucionId'],
  })

export type RespuestaEntrada = z.input<typeof zRespuesta>
export type Respuesta = z.output<typeof zRespuesta>
